import { createContext, useState, useEffect } from 'react';

import { FeatureFlags } from 'src/types/featureFlags';
import { featureFlagsApi } from 'src/repositories/featureFlagsApi';

export const FeatureFlagsContext = createContext<FeatureFlags | null>(null);

const defaultFeatureFlags: FeatureFlags = {
  featureShowOrgComments: false,
  featureShowSuperadminComments: false,
  featureShowAiOrgsideMatching: false,
  featureShowEngagementTerms: false,
  featureShowOrgBrrPricingUpdate: false,
  featureShowModifiedAppForDemoEnv: false,
  featureShowOrgStartRate: false,
  featureUseTalentUsageFee: false,
  featureUseEngagementExtension: false,
  featureUseTalentProfileVideo: false,
  featureShowNewTalentDashboard: false,
  featureShowOrgSavings: false,
  featureAskOrgUserForPoToApproveTheEng: false,
  featureUseNewTalentProfilePictureUpload: false,
  featureAskTalentToUploadProfilePicture: false,
  featureUseW2EngagementTypeForOrg: false,
  featureShowOrgTimeAndBillingReporting: false,
  featureEnableAiDiscoveryOfTalentProfile: false,
  featureEnableAiOpportunityCreation: false,
  featureEnableFteOpportunities: false,
  featureEnableAiGeneratedApplicationQuestions: false,
  featureUseNewApplicantRemovalWorkflow: false,
  featureEnableAiGeneratedApplicantReview: false,
  featureEnableAutoApplicantScreening: false,
  featureHideTaxonomyFromDepartments: false,
  featureRemoveTaxonomyFromAiFeatures: false,
  featureEnableNewTalentOnboarding: false,
  featureEnableAiExtractionOfTalentSpecializations: false,
  featureShowUpdatedSearch: false,
  featureEnableTalentPhoneVerification: false,
  featureEnableAcquiredUsers: false,
  featureEnableTcManagement: false,
  featureEnableW2OnlyOrganizations: false,
  featureEnableReversedRateCalculation: false,
  featureDeductOrgRateFromBillRate: false,
  featureEnablePurchaseOrdersAutofillForTimeEntries: false,
  featureEnableImprovedTimeTracking: false,
  featureEnableEngagementSettingsInheritance: false,
  featureEnableLimitedSuperAdmin: false,
};

let featureFlags: FeatureFlags = defaultFeatureFlags;
export const setFeatureFlags = (flags: FeatureFlags) => {
  featureFlags = { ...featureFlags, ...flags };
};
export const getFeatureFlags = () => {
  return { ...featureFlags };
};

export const FeatureFlagsProvider: React.FunctionComponent = ({ children }) => {
  const [features, setFeatures] = useState<FeatureFlags>(defaultFeatureFlags);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      const apiResponse = await featureFlagsApi.fetchFeatureFlags();

      const fetchedFeatureFlags = Object.fromEntries(
        Object.entries(apiResponse).map(([featureFlagName, featureFlagValueFromApi]) => [
          featureFlagName,
          featureFlagValueFromApi === 'true' || featureFlagValueFromApi === true,
        ]),
      ) as FeatureFlags;

      setFeatures(prevFeatures => {
        const newFeatureFlags: FeatureFlags = {
          ...prevFeatures,
          ...fetchedFeatureFlags,
        };
        setFeatureFlags(newFeatureFlags);
        return newFeatureFlags;
      });
    };

    fetchFeatureFlags();
  }, []);

  return <FeatureFlagsContext.Provider value={features}>{children}</FeatureFlagsContext.Provider>;
};
